import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  OnInit,
  inject,
} from '@angular/core';
import {
  Overlay,
  OverlayRef,
  OverlayPositionBuilder,
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil, debounceTime, tap, delay } from 'rxjs/operators';

@Directive({ selector: '[ag1RevealContentOnHover]' })
export class RevealContentOnHoverDirective implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private overlay = inject(Overlay);
  private positionBuilder = inject(OverlayPositionBuilder);
  private viewContainerRef = inject(ViewContainerRef);

  // subscription cleanup
  private destroy$ = new Subject<void>();

  private overlayRef: OverlayRef | null = null;
  private isMouseOverTrigger = false;
  private isMouseOverOverlay = false;

  // (required) pass in the template you'd like to reveal
  @Input('ag1RevealContentOnHover') template!: TemplateRef<any>;

  // (optional) context data to pass to the template
  @Input() contextData: any = null;

  // (optional) delay before opening the overlay
  @Input() hoverDelay: number = 150;

  // (optional) delay before closing the overlay
  @Input() closeDelay: number = 150;

  // (optional) positions of the overlay
  @Input() positions: ConnectedPosition[] = [
    // on top of the trigger element
    {
      originX: 'center',
      originY: 'center',
      overlayX: 'center',
      overlayY: 'center',
    },
  ];

  constructor() {}

  ngOnInit(): void {
    this.setupHoverEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeOverlay();
  }

  private setupHoverEvents(): void {
    const element = this.elementRef.nativeElement;

    // Mouse enter trigger element - only open after delay if mouse is still over
    fromEvent(element, 'mouseenter')
      .pipe(
        takeUntil(this.destroy$),
        tap(() => (this.isMouseOverTrigger = true)),
        delay(this.hoverDelay), // Wait for specified delay before proceeding
        tap(() => {
          // Only open if mouse is still over trigger
          if (this.isMouseOverTrigger) {
            this.openOverlay();
          }
        }),
      )
      .subscribe();

    // Mouse leave trigger element
    fromEvent(element, 'mouseleave')
      .pipe(
        takeUntil(this.destroy$),
        tap(() => (this.isMouseOverTrigger = false)),
        tap(() => this.checkAndCloseOverlay()),
        delay(this.closeDelay),
      )
      .subscribe();
  }

  private openOverlay(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.createOverlay();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
    });

    const portal = this.createPortal();
    this.overlayRef.attach(portal);

    // Setup overlay hover events
    this.setupOverlayHoverEvents();
  }

  private createOverlay(): FlexibleConnectedPositionStrategy {
    return this.positionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.positions)
      .withPush(true);
  }

  private createPortal(): TemplatePortal<any> {
    // Create context object with the item as the $implicit value
    const context = {
      $implicit: this.contextData,
    };
    return new TemplatePortal(this.template, this.viewContainerRef, context);
  }

  private setupOverlayHoverEvents(): void {
    if (!this.overlayRef) {
      return;
    }

    const overlayElement: HTMLElement = this.overlayRef.overlayElement;

    // Mouse enter overlay
    fromEvent(overlayElement, 'mouseenter')
      .pipe(
        takeUntil(this.destroy$),
        tap(() => (this.isMouseOverOverlay = true)),
      )
      .subscribe();

    // Mouse leave overlay
    fromEvent(overlayElement, 'mouseleave')
      .pipe(
        debounceTime(this.closeDelay),
        takeUntil(this.destroy$),
        tap(() => (this.isMouseOverOverlay = false)),
        tap(() => this.checkAndCloseOverlay()),
        delay(this.closeDelay),
      )
      .subscribe();
  }

  private checkAndCloseOverlay(): void {
    // Close only if mouse is not over trigger or overlay
    if (!this.isMouseOverTrigger && !this.isMouseOverOverlay) {
      this.closeOverlay();
    }
  }

  private closeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }
}
